import { ReactElement, useMemo, useContext } from 'react';
import Image from 'next/image';
import { Heading } from '@appsumo/dorado-react';

import { CartItemProps } from '~/types/cart';
import { useCheckoutState } from '~/hooks/checkout';
import { GlobalContext } from '~/contexts/global';

import { CampaignLineItem } from '~/components/checkout/Summary/CampaignLineItem';
import CartItemPricing from '~/components/checkout/Summary/CartItemPricing';

enum OptionTypes {
  CREDITS = 'credits',
  COUPON_DISCOUNT = 'couponDiscount',
  PLAN_DISCOUNT = 'planDiscount',
}

const isItemFree = (cartItem: CartItemProps): boolean => cartItem.price === 0;

interface DisclaimerBlobProps {
  credits: ReactElement | null;
  couponDiscount: ReactElement | null;
  planDiscount: ReactElement | null;
}

type OptionTypeProps = OptionTypes;

function disclaimerBlobHelper(
  cartItems: Array<CartItemProps>,
): DisclaimerBlobProps {
  const disclaimerOptions = {
    credits: null,
    couponDiscount: null,
    planDiscount: null,
  } as DisclaimerBlobProps;

  // Reduce complexity function
  function isOptionTrue(
    optionType: OptionTypeProps,
    isAllowed: boolean,
    isFreebie: boolean,
  ): boolean {
    return !(disclaimerOptions[optionType] ?? isAllowed) || isFreebie;
  }

  // Loop through all cart items to see if we need to show any disclaimers
  for (const cartItem of cartItems) {
    const { deal } = cartItem;
    const isFreebie = isItemFree(cartItem);

    if (isOptionTrue(OptionTypes.CREDITS, !!deal?.allows_credit, isFreebie)) {
      disclaimerOptions.credits = (
        <div>* Credits cannot be applied to this item</div>
      );
    }

    if (
      isOptionTrue(
        OptionTypes.COUPON_DISCOUNT,
        !!deal?.allows_coupon_discount,
        isFreebie,
      )
    ) {
      disclaimerOptions.couponDiscount = (
        <div>** Coupons cannot be applied to this item.</div>
      );
    }

    if (
      isOptionTrue(
        OptionTypes.PLAN_DISCOUNT,
        !!deal?.allows_plan_discount,
        isFreebie,
      )
    ) {
      disclaimerOptions.planDiscount = (
        <div>*** Plan discounts cannot be applied to this item.</div>
      );
    }

    // Nothing else to check, we have all the disclaimers
    if (
      disclaimerOptions.credits &&
      disclaimerOptions.couponDiscount &&
      disclaimerOptions.planDiscount
    )
      break;
  }

  return disclaimerOptions;
}

export default function CartItemList({
  cartItems = [],
}: Readonly<{
  cartItems: Array<CartItemProps>;
}>): ReactElement {
  const { cart } = useCheckoutState();
  const {
    campaigns: { activeAutoCampaign },
  } = useContext(GlobalContext);

  const nonAutoCampaignCouponApplied =
    !!cart?.coupon?.coupon_id && !cart?.active_auto_campaign_coupon_applied;

  const disclaimerBlob = useMemo(() => {
    const disclaimerOptions = disclaimerBlobHelper(cartItems);

    // No disclaimers to show
    if (
      !(
        disclaimerOptions.credits ||
        disclaimerOptions.couponDiscount ||
        disclaimerOptions.planDiscount
      )
    ) {
      return <></>;
    }

    return (
      <div className="mb-4 text-sm text-grace">
        {disclaimerOptions.credits}
        {disclaimerOptions.couponDiscount}
        {disclaimerOptions.planDiscount}
      </div>
    );
  }, [cartItems]);

  const itemList = useMemo(
    () =>
      cartItems.map((cartItem: CartItemProps) => {
        const { deal, deal_plan } = cartItem;
        const isFreebie = isItemFree(cartItem);

        const disclaimerAsterisks =
          (((!deal?.allows_credit || isFreebie) && ' * ') || '') +
          (((!deal?.allows_coupon_discount || isFreebie) && ' ** ') || '') +
          (((!deal?.allows_plan_discount || isFreebie) && ' *** ') || '');
        const itemSubtext =
          deal?.public_name === deal_plan?.public_name
            ? deal_plan?.plan_category
            : deal_plan?.public_name;

        return (
          <div
            key={deal?.id}
            className="flex items-start pb-4 first-of-type:pt-4"
            data-testid="cart-item"
          >
            <div className="flex">
              <div className="inline-block min-w-[48px]">
                <Image
                  src={`${deal?.image}?width=48&height=30&aspect_ratio=3:2`}
                  alt="deal-thumbnail"
                  width={48}
                  height={32}
                  className="mt-2 rounded"
                />
              </div>
            </div>
            <div className="mx-2 flex flex-col">
              <Heading.H5>
                {deal?.public_name}
                <span className="text-grace">{disclaimerAsterisks}</span>
              </Heading.H5>
              <p className="mt-1 lowercase text-midnight first-letter:uppercase">
                {itemSubtext}
              </p>
              <p className="text-midnight">Quantity: {cartItem.quantity}</p>
              {!nonAutoCampaignCouponApplied &&
                cartItem.is_auto_campaign_eligible && (
                  <CampaignLineItem
                    isApplied={!!cart?.active_auto_campaign_coupon_applied}
                    activeAutoCampaign={activeAutoCampaign}
                  />
                )}
            </div>
            <div className="ml-auto flex flex-col text-right">
              <CartItemPricing
                cartItem={cartItem}
                autoCampaignCouponApplied={
                  !!cart?.active_auto_campaign_coupon_applied
                }
              />
            </div>
          </div>
        );
      }),
    [
      cartItems,
      cart?.active_auto_campaign_coupon_applied,
      nonAutoCampaignCouponApplied,
      activeAutoCampaign,
    ],
  );

  return (
    <>
      {itemList}
      {disclaimerBlob}
    </>
  );
}
